import React from 'react'
import { levelNames, localesInterface } from './main/types'
import { Link } from '@ta-interaktiv/contentbricks'

export const locales: localesInterface = {
  hazards: {
    water: 'Inondation',
    avalanche: 'Avalanche',
    landslide: 'Glissement de terrain',
    rockfall: 'Chute de pierres'
  },
  levels: { slight: 'Faible', mean: 'Moyen', substantial: 'Élevé' },
  perimeter: {
    title: 'Statut des données',
    hint:
      'Les régions partiellement ou non cartographiées sont affichées sur la carte lorsqu’un danger individuel est sélectionné.',
    steps: {
      not: 'non cartographié',
      partial: 'partiellement cartographié / en cours',
      full: 'complètement cartographié / non requis'
    }
  },
  nominatim: {
    noEntry: 'Aucun résultat trouvé',
    position: 'Mon adresse',
    placeholder: "Entrez l'adresse",
    search: 'Cherche...'
  },
  other: {
    titleLevels: 'Niveaux de danger',
    titlePerimeter: 'Statut des données',
    teaserTitle: 'Articles sur le sujet',
    credits: ['Rédaction', 'Datajournalisme', 'Conception']
  },
  map: {
    desktop: {
      activate: 'Naviguer dans la carte',
      deactivate: 'Quitter la carte'
    },
    mobile: {
      activate: 'Naviguer dans la carte',
      deactivate: 'Continuer la lecture'
    }
  },
  sources: { title: 'Sources', key: ['Canton', 'Source', 'Statut'] },
  subsections: { search: 'Trouver une adresse', key: 'Légende' },
  hints: {
    interaction: 'Cliquez ici pour interagir avec la carte',
    hazards: 'Cliquez ici pour afficher un seul danger'
  },
  questions: [
    'Ce contenu était-il informatif?',
    'La mise en forme vous plaît-elle',
    'Comment trouvez-vous le fonctionnement de la carte interactive?'
  ],
  attribution: '© Géodonnées des dangers naturels de chaque canton'
}
export const text: {
  head: { title: JSX.Element; lead: JSX.Element }
  main: JSX.Element
  disclaimer: JSX.Element
  levelmeaning: {
    title: JSX.Element
    sub: JSX.Element
    levels: { [P in levelNames]: JSX.Element }
  }
  zonedefinition: {
    title: JSX.Element
    sub: JSX.Element
    image: JSX.Element
    points: JSX.Element[][]
  }
  contentafter: JSX.Element
  reply: JSX.Element
  technical_head: JSX.Element
  technical: JSX.Element
} = {
  head: {
    title: <>Vivez-vous en zone de danger?</>,
    lead: (
      <>
        En 2019, nous avons publié une première version de la carte des dangers
        naturels. Cette année, nous avons mis à jour et complété cette carte
        avec de nouvelles données. Notre travail révèle que 300’000 bâtiments,
        soit 1,2 millions de personnes en Suisse, sont menacés par les
        avalanches, les chutes de pierres, les inondations, les glissements de
        terrain. Et vous? Votre maison est-elle aussi dans une zone à risque?
        Découvrez-le grâce à notre carte interactive des risques.
      </>
    )
  },
  main: (
    <>
      <p>
        Les cellules enquête et journalisme de données de Tamedia continuent
        leur analyse des dangers naturels en Suisse. Il y a un an, nous avions
        publié une première carte des risques d’avalanche et d’inondation. Nous
        l’avons mise à jour et complétée. Cette nouvelle version répertorie
        désormais aussi les données géophysiques: éboulements, glissements de
        terrain et chutes de pierres.
      </p>
      <p>
        Le risque de glissement de terrain en Suisse couvre, à lui seul, une
        superficie de 959 km2, ce qui correspond à la surface du canton de
        Thurgovie. Les glissements de terrain sont, après les inondations, le
        danger naturel qui menace le plus de bâtiments et d’habitants en Suisse.
      </p>
      <p>
        Nous avons établi notre carte des dangers naturels grâce aux géodonnées
        officielles. En Suisse, ce sont les communes et les cantons qui doivent
        établir la liste des risques potentiels, puis la rendre publique. Nous
        avons ainsi rassemblé ces données disponibles dans chaque canton sur une
        seule carte géographique pour en avoir une vision nationale. Deux
        cantons n’ont pas transmis d’informations: Obwald et Nidwald.
      </p>
      <p>
        Vous pouvez chercher des adresses précises sur cette carte, afin de
        découvrir si vous vivez dans une zone à risque ou si vous passez vos
        vacances dans un lieu considéré comme sûr. Notre analyse montre qu’au
        moins 300’000 bâtiments en Suisse sont menacés et 1,2 million
        d’habitants concernés. Parmi eux, 70’000 personnes sont exposées à un
        danger élevé. Cette situation pourrait rapidement évoluer avec le
        changement climatique, qui fragilise toujours plus notre écosystème.
      </p>
    </>
  ),
  disclaimer: (
    <>
      Avertissement:Les pouvoirs publics évaluent les dangers naturels
      principalement dans les zones habitées, fréquentées par l’homme. Il se
      peut qu’il y ait des dangers existants qui ne soient pas visibles sur
      cette carte, parce que, justement, ils se trouvent dans une zone
      inhabitée. Par ailleurs, ces renseignements sont purement indicatifs. Ils
      ne font pas foi. Seules les données publiées sur les sites internet des
      cantons sont officielles (voir les liens dans les références à la fin de
      l'article).
    </>
  ),
  levelmeaning: {
    title: <>Ce que signifient les niveaux de danger</>,
    sub: (
      <>
        Voici quelques explications pour lire et comprendre cette carte. Notre
        carte indique trois niveaux de danger: élevé, moyen et faible. Les zones
        qui présentent un risque très faible (dit résiduel) ne sont pas
        indiquées. Tous les dangers ne sont pas mentionnés, notamment ceux qui
        sont situés en dehors des zones d’habitation.
      </>
    ),
    levels: {
      substantial: (
        <>
          <b>Danger élevé.</b> Cela signifie que, dans cette zone, «les
          personnes sont en danger à l’intérieur et à l’extérieur des bâtiments»
          et qu’il «faut s’attendre à des destructions soudaines de bâtiments»,
          selon la définition officielle. La construction de nouveaux bâtiments
          est interdite dans ces zones. Les constructions existantes sont
          généralement laissées en place mais leur utilisation ou transformation
          est soumise à condition. Toutefois, le risque ne doit pas être
          augmenté par des extensions ou des conversions. Des restrictions
          d'utilisation sont possibles, notamment dans le cas de bâtiments
          sensibles tels que les écoles, les hôpitaux ou les campings.
        </>
      ),
      mean: (
        <>
          <b>Danger moyen</b> veut dire que «les personnes ne sont guère en
          danger à l’intérieur des bâtiments, mais elles le sont à l’extérieur.
          Il faut s’attendre à des dommages aux bâtiments. Des destructions
          soudaines sont improbables.» Par principe, il n’est pas possible de
          construire dans cette zone, sauf exception et sous certaines
          conditions. Des restrictions d’usage sont rares, mais envisageables
          pour les écoles, hôpitaux et campings.
        </>
      ),
      slight: (
        <>
          <b>Danger faible</b> indique un danger «presque inexistant» pour les
          personnes, mais «des dommages matériels importants peuvent […]
          survenir à l’intérieur des bâtiments». Des restrictions d’usage
          peuvent s’appliquent exceptionnellement aux écoles, hôpitaux et
          campings. Dans cette troisième zone, la moins menacée, les nouvelles
          constructions sont autorisées sous condition.
        </>
      )
    }
  },
  zonedefinition: {
    title: <>Pourquoi certaines zones sont plus dangereuses que d’autres</>,
    sub: (
      <>
        Ce sont l’intensité et la fréquence du risque dans un lieu donné qui
        déterminent sa classification. La combinaison de différents dangers
        (avalanche, éboulement, glissement de terrain et inondation) au même
        endroit augmente ce niveau d’alerte. Le classement se fait sur la base
        d’un calcul de probabilité.
      </>
    ),
    image: (
      <img
        className='visual'
        src={require('images/matrix_fr.svg')}
        width='250px'
      />
    ),
    points: [
      [
        <b>Intensité:</b>,
        <>
          Permet de distinguer entre les processus graduels, comme la lente
          montée d’un lac après de fortes pluies, et des événements brutaux,
          comme l’effondrement d’une falaise ou la crue soudaine d’un ruisseau
          de montagne. Les événements rapides sont considérés comme plus
          dangereux.
        </>
      ],
      [
        <b>Probabilité:</b>,
        <>
          Une distinction temporelle est faite entre les événements qui peuvent
          se manifester tous les trente, cent ou trois cents ans. Une faible
          probabilité n’exclut pas le risque. Une zone menacée par un événement
          rare, par exemple une inondation qui pourrait avoir lieu tous les
          trois cents ans, peut quand même être considérée comme dangereuse.
        </>
      ]
    ]
  },
  contentafter: (
    <>
      <h4 className='title'>Et encore</h4>
      <p>
        Les cartes des risques évoluent au fil du temps. Par exemple, la
        construction d’une digue de protection contre les inondations dans une
        zone considérée comme dangereuse peut conduire à son déclassement.
        L’inverse est aussi possible. La découverte de nouvelles techniques
        d’expertise peut aussi mettre au jour de nouveaux dangers. De plus, les
        techniques d’expertise se complexifient. Avant, les décisions se
        prenaient sur le terrain, par des inspections sur place. Aujourd’hui,
        des modèles informatiques permettent de compléter cette analyse.
      </p>
      <p>
        La carte suisse des dangers est élaborée par Tamedia sur la base des
        géodonnées officielles des cantons qui les mettent à la disposition du
        public, sur internet notamment.
      </p>

      <p>
        Il peut y avoir un décalage entre les informations de Tamedia et celle
        des cantons. Ces derniers doivent en effet mettre les cartes à l’enquête
        publique avant de les valider officiellement. Cette étape prend du
        temps, parfois des années. Ce rythme administratif peut expliquer cette
        différence entre les deux cartes. Dans le doute, ce sont les données
        publiques qui font foi.
      </p>
    </>
  ),
  reply: (
    <>
      <b>Indications à l'éditeur</b>
      <br />
      Vivez-vous dans une zone dangereuse? Connaissez-vous des bâtiments
      sensibles qui sont menacés? Avez-vous des questions sur la thématique?
      L’équipe de journalisme de données vous écoute:{' '}
      <Link href='mailto:daten@tamedia.ch' hideIcon>
        daten@tamedia.ch
      </Link>
    </>
  ),
  technical_head: <>Remarques techniques</>,
  technical: (
    <>
      <p>
        La carte de Tamedia a été élaborée grâce aux géodonnées transmises par
        les cantons. L’échange a été fait par voie électronique. La cellule
        enquête a ensuite homogénéisé le format et la terminologie de ces
        informations pour permettre leur comparaison entre les cantons. Notre
        carte représente l’état de la situation le jour de l’obtention de ces
        données (la date est indiquée dans le tableau ci-dessous). Elle n'est
        pas mise à jour.
      </p>
      <p>
        Les géodonnées peuvent être consultées sur les géoportails cantonaux.
        Les liens vers les sites se trouvent dans le tableau ci-dessous. Dans
        certains cas, les géoportails cantonaux contiennent davantage
        d'informations, comme les dangers potentiels dans les zones hors
        agglomérations. Précisons enfin que certaines parties du territoire
        n’ont pas encore été analysées. Ces zones sont hachurées sur la carte.
      </p>
      <p>
        <Link href='https://www.bafu.admin.ch/bafu/fr/home/themes/dangers-naturels/info-specialistes/situation-de-danger-et-utilisation-du-territoire/donnees-de-base-sur-les-dangers/cartes-de-dangers--cartes-d-intensite-et-cartes-indicatives-des-.html'>
          L’Office fédéral de l'environnement
        </Link>{' '}
        donne également un aperçu des cantons pour lesquels les dangers naturels
        ne sont pas encore cartographiés dans leur intégralité.
      </p>
    </>
  )
}
